import { useState, useMemo } from 'react'
import type {
  ColumnDef,
  ColumnFiltersState,
  SortingState,
  VisibilityState,
  RowSelectionState,
  Row,
} from '@tanstack/react-table'
import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  useReactTable,
} from '@tanstack/react-table'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Checkbox } from '@/components/ui/checkbox'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  MoreHorizontal,
  Pencil,
  Trash2,
  LogIn,
  LogOut,
  Ban,
  UserX,
  XCircle,
} from 'lucide-react'
import {
  DataTableColumnHeader,
  DataTablePagination,
} from '@/components/data-table'
import { useTransientViewStore } from '@/stores/transientViewStore'
import type { TransientStayTableRow, TransientStatus, TransientColumnGroupId, TransientGroupingOption } from '@/types/transient-stays'
import {
  TRANSIENT_STATUS_LABELS,
  TRANSIENT_STATUS_VARIANTS,
  TRANSIENT_COLUMN_GROUPS,
  INVOICE_STATUS_LABELS,
  INVOICE_STATUS_VARIANTS,
  getInvoiceStatus,
  formatCurrency,
  canCheckInTransient,
  canCheckOutTransient,
  canCancelTransient,
  canNoShowTransient,
  canTerminateTransient,
} from '@/types/transient-stays'

interface TransientStaysTableProps {
  data: TransientStayTableRow[]
  isLoading: boolean
  onEdit: (id: string) => void
  onDelete: (id: string, bookingNumber: string) => void
  onRowClick: (id: string) => void
  onCheckIn?: (id: string) => void
  onCheckOut?: (id: string, bookingNumber: string) => void
  onCancel?: (id: string, bookingNumber: string) => void
  onNoShow?: (id: string, bookingNumber: string) => void
  onTerminate?: (id: string, bookingNumber: string) => void
}

// ============================================
// Column visibility from column groups
// ============================================

const COLUMN_TO_GROUP: Record<string, TransientColumnGroupId> = {
  // Identity (always on)
  booking_number: 'identity',
  aircraft: 'identity',
  client: 'identity',
  unit: 'identity',
  date_range: 'identity',
  nights_count: 'identity',
  status: 'identity',
  // Billing (optional toggle)
  rate: 'billing',
  total_amount: 'billing',
  invoice_status: 'billing',
}

function getColumnVisibility(visibleGroups: Set<TransientColumnGroupId>): VisibilityState {
  const visibility: VisibilityState = { select: true, actions: true }

  for (const [colId, groupId] of Object.entries(COLUMN_TO_GROUP)) {
    const group = TRANSIENT_COLUMN_GROUPS.find((g) => g.id === groupId)
    visibility[colId] = group?.alwaysOn || visibleGroups.has(groupId)
  }

  return visibility
}

// ============================================
// Grouping
// ============================================

function groupRows(
  rows: Row<TransientStayTableRow>[],
  groupBy: TransientGroupingOption
): Map<string, Row<TransientStayTableRow>[]> {
  if (groupBy === 'none') {
    return new Map([['all', rows]])
  }

  const groups = new Map<string, Row<TransientStayTableRow>[]>()

  rows.forEach((row) => {
    let groupKey: string

    switch (groupBy) {
      case 'status':
        groupKey = TRANSIENT_STATUS_LABELS[row.original.status]
        break
      case 'space':
        groupKey = row.original.unit_code || 'Not Assigned'
        break
      case 'client':
        groupKey = row.original.client_name || 'Walk-in'
        break
      default:
        groupKey = 'all'
    }

    if (!groups.has(groupKey)) {
      groups.set(groupKey, [])
    }
    groups.get(groupKey)!.push(row)
  })

  return groups
}

// ============================================
// Table Component
// ============================================

export function TransientStaysTable({
  data,
  isLoading,
  onEdit,
  onDelete,
  onRowClick,
  onCheckIn,
  onCheckOut,
  onCancel,
  onNoShow,
  onTerminate,
}: TransientStaysTableProps) {
  const {
    groupBy,
    visibleColumnGroups,
    search,
    setSearch,
  } = useTransientViewStore()

  const [sorting, setSorting] = useState<SortingState>([])
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([])
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({})

  const columnVisibility = useMemo(
    () => getColumnVisibility(visibleColumnGroups),
    [visibleColumnGroups]
  )

  const columns: ColumnDef<TransientStayTableRow>[] = [
    {
      id: 'select',
      header: ({ table }) => (
        <Checkbox
          checked={
            table.getIsAllPageRowsSelected() ||
            (table.getIsSomePageRowsSelected() && 'indeterminate')
          }
          onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
          aria-label="Select all"
          className="translate-y-[2px]"
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          checked={row.getIsSelected()}
          onCheckedChange={(value) => row.toggleSelected(!!value)}
          aria-label="Select row"
          className="translate-y-[2px]"
          onClick={(e) => e.stopPropagation()}
        />
      ),
      enableSorting: false,
      enableHiding: false,
    },
    // ---- Identity group (always on) ----
    {
      accessorKey: 'booking_number',
      header: ({ column }) => <DataTableColumnHeader column={column} title="Booking #" />,
      cell: ({ row }) => (
        <span className="font-mono font-medium">{row.getValue('booking_number')}</span>
      ),
      enableHiding: false,
    },
    {
      accessorKey: 'status',
      header: ({ column }) => <DataTableColumnHeader column={column} title="Status" />,
      cell: ({ row }) => {
        const status = row.getValue('status') as TransientStatus
        return (
          <Badge variant={TRANSIENT_STATUS_VARIANTS[status]}>
            {TRANSIENT_STATUS_LABELS[status]}
          </Badge>
        )
      },
      filterFn: (row, id, value: string[]) => {
        if (!value || value.length === 0) return true
        return value.includes(row.getValue(id))
      },
    },
    {
      id: 'client',
      header: ({ column }) => <DataTableColumnHeader column={column} title="Client" />,
      cell: ({ row }) => {
        const clientName = row.original.client_name
        return clientName ? (
          <span className="font-medium">{clientName}</span>
        ) : (
          <span className="text-muted-foreground">—</span>
        )
      },
    },
    {
      id: 'aircraft',
      header: ({ column }) => <DataTableColumnHeader column={column} title="Aircraft" />,
      cell: ({ row }) => {
        const tailNumber = row.original.tail_number
        const typeName = row.original.aircraft_type_name

        if (tailNumber) {
          return (
            <div>
              <div className="font-mono font-medium">{tailNumber}</div>
              {typeName && (
                <div className="text-xs text-muted-foreground">{typeName}</div>
              )}
            </div>
          )
        }

        return <span className="text-muted-foreground">—</span>
      },
    },
    // ---- Identity group (always on) continued ----
    {
      id: 'unit',
      header: ({ column }) => <DataTableColumnHeader column={column} title="Unit" />,
      cell: ({ row }) => {
        const unitCode = row.original.unit_code
        const buildingName = row.original.building_name

        if (unitCode) {
          return (
            <div>
              <div className="font-medium">{unitCode}</div>
              {buildingName && (
                <div className="text-xs text-muted-foreground">{buildingName}</div>
              )}
            </div>
          )
        }

        return <span className="text-orange-600 dark:text-orange-500 font-medium">Not assigned</span>
      },
    },
    {
      id: 'date_range',
      accessorKey: 'check_in',
      header: ({ column }) => <DataTableColumnHeader column={column} title="Dates" />,
      cell: ({ row }) => {
        const checkIn = row.original.check_in
        const checkOut = row.original.actual_check_out || row.original.expected_check_out
        const fmtShort = (d: string) => new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })

        return (
          <span className="text-sm whitespace-nowrap">
            {fmtShort(checkIn)}
            <span className="text-muted-foreground mx-1">&rarr;</span>
            {checkOut ? fmtShort(checkOut) : <span className="text-yellow-600 dark:text-yellow-500">Open</span>}
          </span>
        )
      },
    },
    {
      accessorKey: 'nights_count',
      header: ({ column }) => <DataTableColumnHeader column={column} title="Nights" />,
      cell: ({ row }) => {
        const nights = row.getValue('nights_count') as number | null
        return nights ? (
          <span className="text-sm">{nights}</span>
        ) : (
          <span className="text-muted-foreground">—</span>
        )
      },
    },
    // ---- Billing group (optional toggle) ----
    {
      id: 'rate',
      header: 'Rate',
      cell: ({ row }) => {
        const rateAmount = row.original.rate_amount
        const rateType = row.original.rate_type
        if (rateAmount == null) return <span className="text-muted-foreground">—</span>
        return (
          <span className="text-sm">
            {formatCurrency(rateAmount)}
            {rateType && <span className="text-muted-foreground text-xs ml-1">/{rateType === 'per_night' ? 'nt' : rateType === 'per_sqft' ? 'sqft' : 'flat'}</span>}
          </span>
        )
      },
    },
    {
      accessorKey: 'total_amount',
      header: ({ column }) => <DataTableColumnHeader column={column} title="Total" />,
      cell: ({ row }) => {
        const total = row.getValue('total_amount') as number | null
        return <span className="font-medium">{formatCurrency(total)}</span>
      },
    },
    {
      id: 'invoice_status',
      header: 'Invoice',
      cell: ({ row }) => {
        const invoiceStatus = getInvoiceStatus(row.original)
        if (!invoiceStatus) return <span className="text-muted-foreground">—</span>
        return (
          <Badge variant={INVOICE_STATUS_VARIANTS[invoiceStatus]}>
            {INVOICE_STATUS_LABELS[invoiceStatus]}
          </Badge>
        )
      },
    },
    // ---- Actions ----
    {
      id: 'actions',
      cell: ({ row }) => {
        const { id, booking_number, status } = row.original
        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                className="h-8 w-8 p-0"
                onClick={(e) => e.stopPropagation()}
              >
                <span className="sr-only">Open menu</span>
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
              <DropdownMenuItem onClick={() => onEdit(id)}>
                <Pencil className="mr-2 h-4 w-4" />
                Edit
              </DropdownMenuItem>
              {canCheckInTransient(status) && onCheckIn && (
                <DropdownMenuItem onClick={() => onCheckIn(id)}>
                  <LogIn className="mr-2 h-4 w-4" />
                  Check In
                </DropdownMenuItem>
              )}
              {canCheckOutTransient(status) && onCheckOut && (
                <DropdownMenuItem onClick={() => onCheckOut(id, booking_number)}>
                  <LogOut className="mr-2 h-4 w-4" />
                  Check Out
                </DropdownMenuItem>
              )}
              {(canCancelTransient(status) || canNoShowTransient(status) || canTerminateTransient(status)) && (
                <DropdownMenuSeparator />
              )}
              {canCancelTransient(status) && onCancel && (
                <DropdownMenuItem
                  onClick={() => onCancel(id, booking_number)}
                  className="text-destructive focus:text-destructive"
                >
                  <Ban className="mr-2 h-4 w-4" />
                  Cancel
                </DropdownMenuItem>
              )}
              {canNoShowTransient(status) && onNoShow && (
                <DropdownMenuItem
                  onClick={() => onNoShow(id, booking_number)}
                  className="text-destructive focus:text-destructive"
                >
                  <UserX className="mr-2 h-4 w-4" />
                  No-Show
                </DropdownMenuItem>
              )}
              {canTerminateTransient(status) && onTerminate && (
                <DropdownMenuItem
                  onClick={() => onTerminate(id, booking_number)}
                  className="text-destructive focus:text-destructive"
                >
                  <XCircle className="mr-2 h-4 w-4" />
                  Terminate
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => onDelete(id, booking_number)}
                className="text-destructive focus:text-destructive"
              >
                <Trash2 className="mr-2 h-4 w-4" />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )
      },
      enableHiding: false,
    },
  ]

  const table = useReactTable({
    data,
    columns,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onRowSelectionChange: setRowSelection,
    onGlobalFilterChange: setSearch,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    globalFilterFn: (row, _columnId, filterValue) => {
      const s = filterValue.toLowerCase()
      const bookingNumber = (row.original.booking_number || '').toLowerCase()
      const clientName = (row.original.client_name || '').toLowerCase()
      const tailNumber = (row.original.tail_number || '').toLowerCase()
      const unitCode = (row.original.unit_code || '').toLowerCase()

      return (
        bookingNumber.includes(s) ||
        clientName.includes(s) ||
        tailNumber.includes(s) ||
        unitCode.includes(s)
      )
    },
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection,
      globalFilter: search,
    },
    initialState: {
      pagination: { pageSize: 15 },
    },
  })

  // Group rows by selected option
  const groupedRows = useMemo(() => {
    const filteredRows = table.getRowModel().rows
    return groupRows(filteredRows, groupBy)
  }, [table.getRowModel().rows, groupBy])

  return (
    <div className="space-y-4">
      {/* Table */}
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead key={header.id} colSpan={header.colSpan}>
                    {header.isPlaceholder
                      ? null
                      : flexRender(header.column.columnDef.header, header.getContext())}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={columns.length} className="h-24 text-center">
                  <div className="text-muted-foreground">Loading transient stays...</div>
                </TableCell>
              </TableRow>
            ) : groupedRows.size === 0 || (groupedRows.size === 1 && groupedRows.get('all')?.length === 0) ? (
              <TableRow>
                <TableCell colSpan={columns.length} className="h-24 text-center">
                  <div className="text-muted-foreground">
                    No transient stays found. Add a booking to get started.
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              Array.from(groupedRows.entries()).map(([groupKey, rows]) => (
                <GroupSection
                  key={groupKey}
                  groupKey={groupKey}
                  groupBy={groupBy}
                  rows={rows}
                  colSpan={table.getVisibleFlatColumns().length}
                  onRowClick={onRowClick}
                />
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <DataTablePagination table={table} />
    </div>
  )
}

// ============================================
// Group Section
// ============================================

function GroupSection({
  groupKey,
  groupBy,
  rows,
  colSpan,
  onRowClick,
}: {
  groupKey: string
  groupBy: TransientGroupingOption
  rows: Row<TransientStayTableRow>[]
  colSpan: number
  onRowClick: (id: string) => void
}) {
  if (groupBy === 'none' || groupKey === 'all') {
    return (
      <>
        {rows.map((row) => (
          <TableRow
            key={row.id}
            data-state={row.getIsSelected() && 'selected'}
            className="cursor-pointer"
            onClick={() => onRowClick(row.original.id)}
          >
            {row.getVisibleCells().map((cell) => (
              <TableCell key={cell.id}>
                {flexRender(cell.column.columnDef.cell, cell.getContext())}
              </TableCell>
            ))}
          </TableRow>
        ))}
      </>
    )
  }

  return (
    <>
      <TableRow className="bg-muted/50 hover:bg-muted/50">
        <TableCell colSpan={colSpan} className="py-2">
          <div className="flex items-center gap-2 font-medium">
            <span>{groupKey}</span>
            <span className="text-muted-foreground font-normal">({rows.length})</span>
          </div>
        </TableCell>
      </TableRow>
      {rows.map((row) => (
        <TableRow
          key={row.id}
          data-state={row.getIsSelected() && 'selected'}
          className="cursor-pointer"
          onClick={() => onRowClick(row.original.id)}
        >
          {row.getVisibleCells().map((cell) => (
            <TableCell key={cell.id}>
              {flexRender(cell.column.columnDef.cell, cell.getContext())}
            </TableCell>
          ))}
        </TableRow>
      ))}
    </>
  )
}
